import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useParams } from 'react-router';
import { Container } from 'yoso';
import { useAdventure } from '~/components/Adventure/Adventure.hooks';
import { AdventureAnnouncement } from '~/components/Adventure/Announcement/AdventureAnnouncement';
import { AdventureExplore } from '~/components/Adventure/Explore/AdventureExplore';
import { AdventureExploreErrorFallback } from '~/components/Adventure/Explore/Error/AdventureExploreErrorFallback';
import { AdventureGuide } from '~/components/Adventure/Guide/AdventureGuide';
import { AdventureHero } from '~/components/Adventure/Hero';
import { AdventureProgress } from '~/components/Adventure/Progress/AdventureProgress';
import { AdventureStatusBar } from '~/components/Adventure/Status/AdventureStatusBar';
import { LoginSecurity } from '~/components/Auth/Login/Security/LoginSecurity';
import { QueryErrorBoundary } from '~/components/Error';
import { useFlag } from '~/components/Flag';
import { Page } from '~/components/Page';
import { Section } from '~/components/Page/Section/Section';
import { HashScroll } from '~/components/Route/Scroll/HashScroll';
import { StatusBarErrorFallback } from '~/components/Status/Bar/ErrorFallback/StatusBarErrorFallback';
import styles from './AdventurePage.module.scss';

export const AdventurePage = () => {
  const { id: adventureId } = useParams<{ id: string }>();
  const id = +adventureId;
  const { data: { status } = {} } = useAdventure(id, { enabled: !!id });
  const { hide } = useFlag({ name: 'ADVENTURE', value: adventureId });

  useEffect(() => {
    if (status === 'IN_PROGRESS') {
      hide();
    }
  }, [status, hide]);

  return (
    <Page className={styles.root} data-event-page_name="Adventure">
      <Container margin="auto">
        <Section className={styles.pageSection}>
          <Section>
            <AdventureHero id={id} />
            <ErrorBoundary
              fallbackRender={(props) => (
                <StatusBarErrorFallback {...props} className={styles.statusBar} />
              )}>
              <LoginSecurity>
                <AdventureStatusBar id={id} className={styles.statusBar} />
              </LoginSecurity>
            </ErrorBoundary>
          </Section>
          <Section>
            <QueryErrorBoundary>
              <AdventureProgress id={id} />
            </QueryErrorBoundary>
          </Section>
          <QueryErrorBoundary FallbackComponent={AdventureExploreErrorFallback}>
            <Section>
              <AdventureExplore id={id} />
            </Section>
          </QueryErrorBoundary>
          <HashScroll hash="#announcement" behavior="smooth" position="start">
            <Section>
              <AdventureAnnouncement />
            </Section>
          </HashScroll>
          <Section>
            <AdventureGuide />
          </Section>
        </Section>
      </Container>
    </Page>
  );
};
